/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { useAuth } from '@slr/shared-library';
import { COMPLETE_REGISTRATION_PATH } from 'configs';
import { useQueryParam } from 'providers';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
    buildInvitationSearch,
    resolvePendingInvitation,
    storePendingInvitation
} from 'utils/pendingInvitation';
import { useFindMembershipsOfUser } from '../organization/useFindMembershipsOfUser';
import { useGetCurrentUserProfileWithSolution1 } from '../user/useGetCurrentUserProfileWithSolution1';
import { userNeedsRegistrationStepTwo } from './userNeedsRegistrationStepTwo';

export const useProfileCompletionCheck = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { invitationQuery } = useQueryParam();

  const {
    data: userProfile,
    isPending: isProfilePending,
    error
  } = useGetCurrentUserProfileWithSolution1();

  const {
    data: memberships,
    isPending: isMembershipsPending
  } = useFindMembershipsOfUser();

  const isPending = isProfilePending || isMembershipsPending;

  useEffect(() => {
    if (!isAuthenticated || location.pathname === COMPLETE_REGISTRATION_PATH) {
      return;
    }

    if (isPending) {
      return;
    }

    if (!userNeedsRegistrationStepTwo(userProfile, memberships)) {
      return;
    }

    const pendingInvitation = resolvePendingInvitation(
      location.search,
      invitationQuery
    );
    if (pendingInvitation) {
      storePendingInvitation(pendingInvitation);
    }
    const invitationSearch = pendingInvitation
      ? buildInvitationSearch(pendingInvitation)
      : '';
    navigate(`${COMPLETE_REGISTRATION_PATH}${invitationSearch}`, {
      replace: true
    });
  }, [
    isAuthenticated,
    userProfile,
    memberships,
    isPending,
    navigate,
    location.pathname,
    location.search,
    invitationQuery
  ]);

  return {
    isProfileComplete: !userNeedsRegistrationStepTwo(userProfile, memberships),
    isPending,
    error
  };
};
